import React, { useState, useCallback, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  EnvelopeClosedIcon,
  LockClosedIcon,
  ArrowRightIcon,
} from '@radix-ui/react-icons';
import { useAuthStore } from '../../store/authStore';
import { Input, PasswordInput, Button } from '../../components';
import { AuthLayout } from './AuthLayout';
import { validateEmail } from '../../utils/validation';
import { useEmailValidation } from '../../hooks/useEmailValidation';
import { formatApiError, getFieldErrorsFromApi } from '../../utils/errorHandler';
import './Auth.scss';

interface ValidationErrors {
  password?: string;
}

interface LoginLocationState {
  email?: string;
}

export const Login: React.FC = () => {
  const { login, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  const sessionExpired = searchParams.get('sessionExpired') === 'true';
  const locationState = location.state as LoginLocationState | null;
  const resetEmail = searchParams.get('email') || locationState?.email || undefined;
  const [email, setEmail] = useState(resetEmail || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<{ password?: boolean }>({});
  const {
    error: emailError,
    onBlur: onEmailBlur,
    onChange: onEmailChange,
    validate: validateEmailField,
    setError: setEmailError,
  } = useEmailValidation();

  useEffect(() => {
    if (!error) return;

    const fieldErrors = getFieldErrorsFromApi(error);
    if (fieldErrors.email) {
      setEmailError(fieldErrors.email);
    }
    if (fieldErrors.password) {
      setValidationErrors(prev => ({
        ...prev,
        password: fieldErrors.password,
      }));
    }
  }, [error, setEmailError]);

  const validate = useCallback((): boolean => {
    const emailResult = validateEmailField(email);
    const errors: ValidationErrors = {};

    if (!password) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return emailResult.valid && Object.keys(errors).length === 0;
  }, [email, password, validateEmailField]);

  const handlePasswordBlur = useCallback(() => {
    setTouched(prev => ({ ...prev, password: true }));
    setValidationErrors(prev => ({
      ...prev,
      password: password ? undefined : 'Password is required',
    }));
  }, [password]);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      clearError();
      setEmail(e.target.value);
      onEmailChange(e.target.value);
    },
    [clearError, onEmailChange]
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      clearError();
      setPassword(e.target.value);
      if (touched.password) {
        setValidationErrors(prev => ({
          ...prev,
          password: e.target.value ? undefined : 'Password is required',
        }));
      }
    },
    [clearError, touched.password]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ password: true });

    if (!validate()) {
      return;
    }

    const emailResult = validateEmailField(email);
    try {
      await login({
        email: emailResult.trimmed,
        password,
        rememberMe,
      });
    } catch {
      // Error state is handled by authStore
    }
  };

  const handleContinuePasswordReset = useCallback(() => {
    navigate(`/reset-password/set?token=${encodeURIComponent(resetToken ?? '')}`);
  }, [navigate, resetToken]);

  const apiFieldErrors = error ? getFieldErrorsFromApi(error) : {};
  const showApiBanner =
    error &&
    !apiFieldErrors.email &&
    !apiFieldErrors.password;

  const canSubmit =
    validateEmail(email).valid &&
    password.length > 0 &&
    !emailError &&
    !validationErrors.password;

  const getSubmitDisabledReason = (): string | undefined => {
    if (isLoading) return 'Signing in...';
    if (emailError) return emailError;
    if (validationErrors.password) return validationErrors.password;
    const emailResult = validateEmail(email);
    if (!emailResult.valid) return emailResult.error;
    if (!password) return 'Password is required';
    return undefined;
  };

  return (
    <AuthLayout tagline="Your day, organized. Your goals, within reach.">
      <div className="auth-card">
        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <h1 className="auth-title">Welcome Back</h1>
          <p className="auth-subtitle">
            Enter your credentials to access your dashboard
          </p>

          {sessionExpired && (
            <div className="auth-notice auth-notice--warning" role="alert">
              Your session has expired. Please sign in again to continue.
            </div>
          )}

          {resetToken && (
            <div className="auth-notice auth-notice--action">
              <p>You&apos;re resetting your password. Continue to choose a new password.</p>
              <Button
                type="button"
                fullWidth
                className="auth-submit-button auth-submit-button--compact"
                onClick={handleContinuePasswordReset}
              >
                Set New Password
                <ArrowRightIcon aria-hidden="true" />
              </Button>
            </div>
          )}

          <div className="auth-field">
            <label htmlFor="login-email" className="auth-field-label">
              <EnvelopeClosedIcon className="auth-field-icon" aria-hidden="true" />
              Email Address
            </label>
            <Input
              id="login-email"
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={handleEmailChange}
              onBlur={() => onEmailBlur(email)}
              error={emailError}
              autoComplete="email"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="login-password" className="auth-field-label">
              <LockClosedIcon className="auth-field-icon" aria-hidden="true" />
              Password
            </label>
            <PasswordInput
              id="login-password"
              placeholder="••••••••"
              value={password}
              onChange={handlePasswordChange}
              onBlur={handlePasswordBlur}
              error={validationErrors.password}
              autoComplete="current-password"
            />
          </div>

          <div className="auth-options">
            <label className="auth-remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>Remember me for 30 days</span>
            </label>
            <Link
              to="/reset-password"
              state={{ email: email.trim() || undefined }}
              className="auth-text-link"
            >
              Forgot Password?
            </Link>
          </div>

          {showApiBanner && (
            <div className="auth-error" role="alert">
              {formatApiError(error)}
            </div>
          )}

          <Button
            type="submit"
            fullWidth
            className="auth-submit-button"
            disabled={isLoading || !canSubmit}
            disabledReason={getSubmitDisabledReason()}
          >
            {isLoading ? 'Signing In...' : 'Log In'}
            {!isLoading && <ArrowRightIcon aria-hidden="true" />}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
};
